import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../components/auth/AuthContext';
import * as Sentry from "@sentry/react";
import { 
  Loader2, 
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  RefreshCw
} from 'lucide-react';

// Import shared utilities (from BillingUtils.js)
import { 
  useResponsive,
  formatDate,
  safeParseFloat,
  currencyUtils,
  logError,
  logInfo,
  logUserAction,
  logPerformance,
  StatusBadge,
  Pagination,
  getNetworkErrorMessage 
} from './BillingUtils';

import { useNotification } from '../../Notifications';

// Date validation constants
const SERVICE_START_DATE = '2025-01-01';

// Helper function to get current date in YYYY-MM-DD format
const getCurrentDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Helper function to validate date
const validateDate = (dateString) => {
  if (!dateString) return { valid: true };
  
  const date = new Date(dateString + 'T00:00:00');
  const minDate = new Date(SERVICE_START_DATE + 'T00:00:00');
  const maxDate = new Date(getCurrentDate() + 'T00:00:00');
  
  if (isNaN(date.getTime())) {
    return { valid: false, error: 'Invalid date format' };
  }
  
  if (date < minDate) {
    return { valid: false, error: `Date cannot be before ${SERVICE_START_DATE}` };
  }
  
  if (date > maxDate) {
    return { valid: false, error: 'Date cannot be in the future' };
  }
  
  return { valid: true };
};

// Helper function to validate date range
const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return { valid: true };
  
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  
  if (start > end) {
    return { valid: false, error: 'Start date must be less than or equal to end date' };
  }
  
  return { valid: true };
};

// Error Messages - Complete backend coverage
const ERROR_MESSAGES = {
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'AUTH_ERROR': 'Please log in again to continue',
  'ACCESS_DENIED': 'You do not have permission to perform this action',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later',
  'NETWORK_ERROR': 'Network connection error. Please check your connection and try again',
  'SERVER_ERROR': 'An internal error occurred. Please try again later',
  'INVALID_JSON': 'Invalid request format. Please try again.',
  'USER_NOT_FOUND': 'User profile not found. Please refresh and try again.',
  'INVALID_USER_PROFILE': 'Invalid user profile format. Please contact support.',
  'INCOMPLETE_PROFILE': 'Please complete your profile before accessing volume billing.',
  'INVALID_DATE_FORMAT': 'Invalid date format. Use YYYY-MM-DD.',
  'INVALID_DATE_RANGE': 'Start date must be less than or equal to end date.',
  'INVALID_SIZE_FILTER': 'Invalid size filter. Allowed values: small, medium, large.',
  'INVALID_STATUS': 'Invalid status filter. Allowed values: available, deleted, active, completed.',
  'INVALID_TIER': 'Invalid tier filter. Allowed values: Advanced, Legacy.',
  'INVALID_MIN_MINUTES': 'Minimum minutes must be a positive integer.',
  'INVALID_MAX_MINUTES': 'Maximum minutes must be a positive integer.',
  'INVALID_MINUTE_RANGE': 'Minimum minutes cannot be greater than maximum minutes.',
  'INVALID_MIN_COST': 'Minimum cost must be a positive number.',
  'INVALID_MAX_COST': 'Maximum cost must be a positive number.',
  'INVALID_COST_RANGE': 'Minimum cost must be less than or equal to maximum cost.',
  'CURRENCY_NOT_SET': 'Please set your preferred currency in account settings.',
  'SERVICE_ERROR': 'Service error. Please contact support.',
  'SESSION_EXPIRED': 'Your session has expired. Please sign in again.',
  'INSUFFICIENT_CREDITS': 'Insufficient account credits',
  // Network Errors (add after existing entries)
'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again.",
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  // Check specific codes FIRST
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Check HTTP status codes
  if (error.response?.status >= 500 || error.status >= 500) {
    return ERROR_MESSAGES['SERVICE_UNAVAILABLE'];
  }
  if (error.response?.status === 429 || error.status === 429) {
    const retryAfter = error.response?.headers['retry-after'] || 60;
    return `Too many requests. Please wait ${retryAfter} seconds before trying again.`;
  }
  if (error.response?.status === 401 || error.status === 401) {
    return ERROR_MESSAGES['SESSION_EXPIRED'];
  }
  if (error.response?.status === 402 || error.status === 402) {
    return ERROR_MESSAGES['INSUFFICIENT_CREDITS'];
  }
  
  // Generic fallbacks LAST
  if (error.userMessage) {
    return error.userMessage;
  }
  if (error.error) {
    return error.error;
  }
  
  return 'Something went wrong. Please try again.';
};

// Performance: Debounce function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

const VolumeSmartFilterContainer = ({ filters, setFilters, applyFilters, resetFilters, isMobile, isTablet }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const dropdownRef = useRef(null);
  const filterBoxRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dateValidationError, setDateValidationError] = useState('');
  
  // Track temporary filter state before applying
  const [tempFilters, setTempFilters] = useState({
    duration: filters.duration,
    size: filters.size,
    status: filters.status,
    tier: filters.tier,
    startDate: filters.startDate,
    endDate: filters.endDate
  });
  
  useEffect(() => {
    // Update tempFilters when filters change externally
    setTempFilters({
      duration: filters.duration,
      size: filters.size,
      status: filters.status,
      tier: filters.tier,
      startDate: filters.startDate,
      endDate: filters.endDate
    });
  }, [filters]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update dropdown position when it opens
  useEffect(() => {
    if (isOpen && filterBoxRef.current) {
      const rect = filterBoxRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  }, [isOpen]);
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    
    logUserAction('volume_filter_dropdown_toggled', {
      isOpen: !isOpen,
      currentFilters: filters,
      isMobile,
      isTablet
    });
  };
  
  const toggleSection = (section) => {
    if (activeSection === section) {
      setActiveSection(null);
    } else {
      setActiveSection(section);
    }
    
    logUserAction('volume_filter_section_toggled', {
      section,
      wasActive: activeSection === section
    });
  };
  
  const handleFilterChange = (type, value) => {
    setTempFilters(prev => ({
      ...prev,
      [type]: value
    }));
    
    // Validate dates immediately when they change
    if (type === 'startDate' || type === 'endDate') {
      const newStartDate = type === 'startDate' ? value : tempFilters.startDate;
      const newEndDate = type === 'endDate' ? value : tempFilters.endDate;
      
      // Clear previous errors
      setDateValidationError('');
      
      // Validate the changed date
      const dateValidation = validateDate(value);
      if (!dateValidation.valid) {
        setDateValidationError(dateValidation.error);
        return;
      }
      
      // Validate date range if both dates are set
      if (newStartDate && newEndDate) {
        const rangeValidation = validateDateRange(newStartDate, newEndDate);
        if (!rangeValidation.valid) {
          setDateValidationError(rangeValidation.error);
          return;
        }
      }
    }
    
    logUserAction('volume_filter_temp_changed', {
      filterType: type,
      value,
      previousValue: tempFilters[type]
    });
  };
  
  const handleApplyFilters = () => {
    // Validate dates before applying
    const startDateValidation = validateDate(tempFilters.startDate);
    if (!startDateValidation.valid) {
      setDateValidationError(startDateValidation.error);
      return;
    }
    
    const endDateValidation = validateDate(tempFilters.endDate);
    if (!endDateValidation.valid) {
      setDateValidationError(endDateValidation.error);
      return;
    }
    
    const rangeValidation = validateDateRange(tempFilters.startDate, tempFilters.endDate);
    if (!rangeValidation.valid) {
      setDateValidationError(rangeValidation.error);
      return;
    }
    
    // Clear validation errors
    setDateValidationError('');
    
    // Apply the tempFilters
    setFilters(tempFilters);
    applyFilters(tempFilters, false); // false indicates not a page change
    setIsOpen(false);
    
    logUserAction('volume_filters_applied', {
      appliedFilters: tempFilters,
      previousFilters: filters
    });
  };
  
  const handleResetFilters = () => {
    resetFilters();
    setTempFilters({
      duration: '',
      size: '',
      status: '',
      tier: '',
      startDate: '',
      endDate: ''
    });
    setDateValidationError('');
    setIsOpen(false);
    
    logUserAction('volume_filters_reset', {
      previousFilters: filters
    });
  };
  
  // Helper to get display text for active filters
  const getActiveFiltersText = () => {
    const activeFilters = [];
    
    if (filters.duration) {
      const durationLabels = {
        'short': 'Short Duration (<1h)',
        'medium': 'Medium Duration (1-24h)', 
        'long': 'Long Duration (>24h)'
      };
      activeFilters.push(durationLabels[filters.duration]);
    }
    
    if (filters.size) {
      const sizeLabels = {
        'small': 'Small Size (<500GB)',
        'medium': 'Medium Size (500-5000GB)', 
        'large': 'Large Size (>5000GB)'
      };
      activeFilters.push(sizeLabels[filters.size]);
    }
    
    if (filters.status) {
      activeFilters.push(`Status: ${filters.status}`);
    }
    
    if (filters.tier) {
      activeFilters.push(`Tier: ${filters.tier}`);
    }
    
    if (filters.startDate && filters.endDate) {
      activeFilters.push(`Date: ${filters.startDate} to ${filters.endDate}`);
    } else if (filters.startDate) {
      activeFilters.push(`From: ${filters.startDate}`);
    } else if (filters.endDate) {
      activeFilters.push(`Until: ${filters.endDate}`);
    }
    
    return activeFilters.length > 0 
      ? activeFilters.join(', ') 
      : 'Filter Volumes';
  };

  return (
    <div ref={dropdownRef} style={{
      backgroundColor: '#1a1a1a',
      borderRadius: '10px',
      border: '1px solid #333333',
      padding: isMobile ? '12px' : '20px',
      paddingBottom: isMobile ? '12px' : 'calc(20px + 0.35cm)',
      width: '100%',
      position: 'relative'
    }}>
      <div style={{
        fontSize: isMobile ? '12px' : '14px',
        color: '#a0a0a0',
        fontWeight: '500',
        marginBottom: isMobile ? '8px' : '10px'
      }}>
        Volume Filter
      </div>
      
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '0',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <div 
          ref={filterBoxRef}
          onClick={toggleDropdown}
          style={{
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center',
            padding: isMobile ? '12px' : '8px 12px',
            backgroundColor: '#252525',
            borderRadius: '6px',
            border: '1px solid #333333',
            width: isMobile ? '100%' : '85%'
          }}
        >
          <div style={{
            fontSize: isMobile ? '13px' : '14px',
            color: filters.duration || filters.size || filters.status || filters.tier || filters.startDate || filters.endDate 
              ? 'white' 
              : '#a0a0a0'
          }}>
            {getActiveFiltersText()}
          </div>
          <div style={{marginLeft: 'auto'}}>
            {isOpen ? (
              <ChevronUp size={isMobile ? 14 : 16} color="#a0a0a0" />
            ) : (
              <ChevronDown size={isMobile ? 14 : 16} color="#a0a0a0" />
            )}
          </div>
        </div>
        
        <button 
          onClick={handleResetFilters}
          style={{
            backgroundColor: '#333333',
            border: 'none',
            borderRadius: '50%',
            width: isMobile ? '40px' : '32px',
            height: isMobile ? '40px' : '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            marginLeft: isMobile ? '0' : '8px',
            alignSelf: isMobile ? 'center' : 'auto'
          }}
          title="Reset Filters"
        >
          <RefreshCw size={isMobile ? 18 : 16} color="white" />
        </button>
      </div>
      
      {isOpen && (
        <div style={{
          position: 'fixed',
          zIndex: 100,
          backgroundColor: '#252525',
          border: '1px solid #333333',
          borderRadius: '6px',
          padding: isMobile ? '12px' : '16px',
          width: isMobile ? '90vw' : '300px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
          top: `${dropdownPosition.top}px`,
          left: isMobile ? '5vw' : `${dropdownPosition.left}px`
        }}>
          {/* Date Validation Error */}
          {dateValidationError && (
            <div style={{
              backgroundColor: 'rgba(238, 85, 85, 0.1)',
              border: '1px solid rgba(238, 85, 85, 0.3)',
              borderRadius: '4px',
              padding: isMobile ? '8px' : '10px',
              marginBottom: isMobile ? '12px' : '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertTriangle size={14} color="#ff7777" />
              <span style={{
                fontSize: isMobile ? '12px' : '13px',
                color: '#ff9999'
              }}>
                {dateValidationError}
              </span>
            </div>
          )}
          
          {/* Date Range Section */}
          <div style={{marginBottom: isMobile ? '12px' : '14.4px'}}>
            <div 
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: isMobile ? '8px 0' : '7.2px 0',
                borderBottom: '1px solid #333333'
              }}
              onClick={() => toggleSection('date')}
            >
              <div style={{fontWeight: '500', fontSize: isMobile ? '14px' : '12.6px', color: 'white'}}>
                Date Range
              </div>
              <div>
                {activeSection === 'date' ? (
                  <ChevronUp size={isMobile ? 16 : 14} color="#a0a0a0" />
                ) : (
                  <ChevronDown size={isMobile ? 16 : 14} color="#a0a0a0" />
                )}
              </div>
            </div>
            
            {activeSection === 'date' && (
              <div style={{
                marginTop: isMobile ? '12px' : '10.8px', 
                display: 'flex', 
                gap: isMobile ? '8px' : '10.8px',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <div style={{flex: 1}}>
                  <div style={{fontSize: isMobile ? '12px' : '10.8px', color: '#a0a0a0', marginBottom: isMobile ? '4px' : '3.6px'}}>From</div>
                  <input 
                    type="date" 
                    value={tempFilters.startDate}
                    min={SERVICE_START_DATE}
                    max={getCurrentDate()}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    style={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333333',
                      borderRadius: '4px',
                      padding: isMobile ? '10px' : '7.2px',
                      color: 'white',
                      width: '100%',
                      fontSize: isMobile ? '14px' : '12.6px'
                    }}
                  />
                </div>
                <div style={{flex: 1}}>
                  <div style={{fontSize: isMobile ? '12px' : '10.8px', color: '#a0a0a0', marginBottom: isMobile ? '4px' : '3.6px'}}>To</div>
                  <input 
                    type="date" 
                    value={tempFilters.endDate}
                    min={SERVICE_START_DATE}
                    max={getCurrentDate()}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    style={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333333',
                      borderRadius: '4px',
                      padding: isMobile ? '10px' : '7.2px',
                      color: 'white',
                      width: '100%',
                      fontSize: isMobile ? '14px' : '12.6px'
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Duration Section */}
          <div style={{marginBottom: isMobile ? '14px' : '16px'}}>
            <div 
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: isMobile ? '8px 0' : '8px 0',
                borderBottom: '1px solid #333333'
              }}
              onClick={() => toggleSection('duration')}
            >
              <div style={{fontWeight: '500', fontSize: isMobile ? '14px' : '14px', color: 'white'}}>
                Duration
              </div>
              <div>
                {activeSection === 'duration' ? (
                  <ChevronUp size={isMobile ? 16 : 16} color="#a0a0a0" />
                ) : (
                  <ChevronDown size={isMobile ? 16 : 16} color="#a0a0a0" />
                )}
              </div>
            </div>
            
            {activeSection === 'duration' && (
              <div style={{marginTop: isMobile ? '12px' : '12px'}}>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '10px' : '8px',
                  marginBottom: isMobile ? '10px' : '8px'
                }}>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.duration === 'short' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.duration === 'short' ? '#e0eacc' : 'white',
                      border: tempFilters.duration === 'short' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('duration', tempFilters.duration === 'short' ? '' : 'short')}
                  >
                    Short (&lt;1h)
                  </button>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.duration === 'medium' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.duration === 'medium' ? '#e0eacc' : 'white',
                      border: tempFilters.duration === 'medium' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('duration', tempFilters.duration === 'medium' ? '' : 'medium')}
                  >
                    Medium (1-24h)
                  </button>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.duration === 'long' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.duration === 'long' ? '#e0eacc' : 'white',
                      border: tempFilters.duration === 'long' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('duration', tempFilters.duration === 'long' ? '' : 'long')}
                  >
                    Long (&gt;24h)
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Size Section */}
          <div style={{marginBottom: isMobile ? '14px' : '16px'}}>
            <div 
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: isMobile ? '8px 0' : '8px 0',
                borderBottom: '1px solid #333333'
              }}
              onClick={() => toggleSection('size')}
            >
              <div style={{fontWeight: '500', fontSize: isMobile ? '14px' : '14px', color: 'white'}}>
                Volume Size
              </div>
              <div>
                {activeSection === 'size' ? (
                  <ChevronUp size={isMobile ? 16 : 16} color="#a0a0a0" />
                ) : (
                  <ChevronDown size={isMobile ? 16 : 16} color="#a0a0a0" />
                )}
              </div>
            </div>
            
            {activeSection === 'size' && (
              <div style={{marginTop: isMobile ? '12px' : '12px'}}>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '10px' : '8px',
                  marginBottom: isMobile ? '10px' : '8px'
                }}>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.size === 'small' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.size === 'small' ? '#e0eacc' : 'white',
                      border: tempFilters.size === 'small' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('size', tempFilters.size === 'small' ? '' : 'small')}
                  >
                    Small (&lt;500GB)
                  </button>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.size === 'medium' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.size === 'medium' ? '#e0eacc' : 'white',
                      border: tempFilters.size === 'medium' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('size', tempFilters.size === 'medium' ? '' : 'medium')}
                  >
                    Medium (500-5000GB)
                  </button>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.size === 'large' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.size === 'large' ? '#e0eacc' : 'white',
                      border: tempFilters.size === 'large' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('size', tempFilters.size === 'large' ? '' : 'large')}
                  >
                    Large (&gt;5000GB)
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Status Section */}
          <div style={{marginBottom: isMobile ? '14px' : '16px'}}>
            <div 
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: isMobile ? '8px 0' : '8px 0',
                borderBottom: '1px solid #333333'
              }}
              onClick={() => toggleSection('status')}
            >
              <div style={{fontWeight: '500', fontSize: isMobile ? '14px' : '14px', color: 'white'}}>
                Status
              </div>
              <div>
                {activeSection === 'status' ? (
                  <ChevronUp size={isMobile ? 16 : 16} color="#a0a0a0" />
                ) : (
                  <ChevronDown size={isMobile ? 16 : 16} color="#a0a0a0" />
                )}
              </div>
            </div>
            
            {activeSection === 'status' && (
              <div style={{marginTop: isMobile ? '12px' : '12px'}}>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '10px' : '8px',
                  marginBottom: isMobile ? '10px' : '8px'
                }}>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.status === 'available' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.status === 'available' ? '#e0eacc' : 'white',
                      border: tempFilters.status === 'available' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('status', tempFilters.status === 'available' ? '' : 'available')}
                  >
                    Available
                  </button>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.status === 'deleted' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.status === 'deleted' ? '#e0eacc' : 'white',
                      border: tempFilters.status === 'deleted' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('status', tempFilters.status === 'deleted' ? '' : 'deleted')}
                  >
                    Deleted
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Tier Section */}
          <div style={{marginBottom: isMobile ? '14px' : '16px'}}>
            <div 
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: isMobile ? '8px 0' : '8px 0',
                borderBottom: '1px solid #333333'
              }}
              onClick={() => toggleSection('tier')}
            >
              <div style={{fontWeight: '500', fontSize: isMobile ? '14px' : '14px', color: 'white'}}>
                Tier
              </div>
              <div>
                {activeSection === 'tier' ? (
                  <ChevronUp size={isMobile ? 16 : 16} color="#a0a0a0" />
                ) : (
                  <ChevronDown size={isMobile ? 16 : 16} color="#a0a0a0" />
                )}
              </div>
            </div>
            
            {activeSection === 'tier' && (
              <div style={{marginTop: isMobile ? '12px' : '12px'}}>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '10px' : '8px',
                  marginBottom: isMobile ? '10px' : '8px'
                }}>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.tier === 'Advanced' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.tier === 'Advanced' ? '#e0eacc' : 'white',
                      border: tempFilters.tier === 'Advanced' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('tier', tempFilters.tier === 'Advanced' ? '' : 'Advanced')}
                  >
                    Advanced
                  </button>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.tier === 'Legacy' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.tier === 'Legacy' ? '#e0eacc' : 'white',
                      border: tempFilters.tier === 'Legacy' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('tier', tempFilters.tier === 'Legacy' ? '' : 'Legacy')}
                  >
                    Legacy
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Apply Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: isMobile ? '14px' : '16px'
          }}>
            <button 
              style={{
                backgroundColor: dateValidationError ? '#666666' : '#e0eacc',
                color: dateValidationError ? '#999999' : 'black',
                border: 'none',
                borderRadius: '4px',
                padding: isMobile ? '12px 20px' : '8px 16px',
                fontWeight: '500',
                cursor: dateValidationError ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '14px' : '13px',
                width: isMobile ? '100%' : 'auto'
              }}
              onClick={handleApplyFilters}
              disabled={!!dateValidationError}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ... rest of the component remains exactly the same ...

const MobileVolumeCard = ({ record, currency }) => {
  const { isMobile } = useResponsive();
  const [isExpanded, setIsExpanded] = useState(false);

  const calculateDuration = (durationMinutes) => {
    try {
      if (durationMinutes !== undefined && durationMinutes !== null) {
        const minutes = parseInt(durationMinutes, 10);
        if (isNaN(minutes)) return 'N/A';
        
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = Math.round(minutes % 60);
        return `${hours}h ${remainingMinutes}m`;
      }
      
      return 'N/A';
    } catch (error) {
      return 'N/A';
    }
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
    
    logUserAction('mobile_volume_card_toggled', {
      volumeId: record.volume_id,
      billingId: record.billing_id,
      isExpanded: !isExpanded
    });
  };

  return (
    <div style={{
      backgroundColor: '#1a1a1a',
      borderRadius: '8px',
      border: '1px solid #333333',
      padding: isMobile ? '14px' : '16px',
      marginBottom: isMobile ? '10px' : '12px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: isMobile ? '6px' : '8px'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: isMobile ? '11px' : '12px',
            color: '#a0a0a0',
            marginBottom: '3px',
            fontFamily: 'monospace'
          }}>
            {sanitizeText(record.billing_id)}
          </div>
          <div style={{
            fontSize: isMobile ? '13px' : '14px',
            color: 'white',
            fontWeight: '500',
            marginBottom: '3px'
          }}>
            {sanitizeText(record.volume_name)} ({record.size_gb} GB)
          </div>
          <div style={{
            fontSize: isMobile ? '11px' : '12px',
            color: '#a0a0a0'
          }}>
            {formatDate(record.start_time, record.currency)}
          </div>
        </div>
        <div style={{
          textAlign: 'right',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '3px'
        }}>
          <div style={{ fontSize: isMobile ? '15px' : '16px', fontWeight: '600', color: '#e0eacc' }}>
            {currencyUtils.getSymbol(record.currency || currency)}
            {safeParseFloat(record.total_cost).toFixed(5)}
          </div>
          <StatusBadge status={record.status} />
        </div>
      </div>
      
      <div style={{
        fontSize: isMobile ? '12px' : '13px',
        color: '#e0e0e0',
        marginBottom: isMobile ? '6px' : '8px',
        lineHeight: '1.3'
      }}>
        Duration: {calculateDuration(record.total_duration_minutes)}
      </div>
      
      <button
        onClick={handleToggleExpand}
        style={{
          backgroundColor: 'transparent',
          border: '1px solid #333333',
          borderRadius: '4px',
          padding: isMobile ? '8px 12px' : '6px 12px',
          color: '#a0a0a0',
          fontSize: isMobile ? '11px' : '12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          width: isMobile ? '100%' : 'auto',
          justifyContent: isMobile ? 'center' : 'flex-start'
        }}
      >
        {isExpanded ? 'Less Details' : 'More Details'}
        {isExpanded ? <ChevronUp size={isMobile ? 14 : 12} /> : <ChevronDown size={isMobile ? 14 : 12} />}
      </button>
      
      {isExpanded && (
        <div style={{
          marginTop: isMobile ? '10px' : '12px',
          paddingTop: isMobile ? '10px' : '12px',
          borderTop: '1px solid #333333'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: isMobile ? '10px' : '8px',
            fontSize: isMobile ? '11px' : '12px'
          }}>
            <div>
              <span style={{ color: '#a0a0a0' }}>Volume ID:</span>
              <div style={{ marginTop: '2px', color: 'white', wordBreak: 'break-all' }}>
                {sanitizeText(record.volume_id)}
              </div>
            </div>
            <div>
              <span style={{ color: '#a0a0a0' }}>Rate:</span>
              <div style={{ marginTop: '2px', color: 'white' }}>
                {currencyUtils.getSymbol(record.currency || currency)}
                {safeParseFloat(record.per_minute_rate || 0).toFixed(5)}/min
              </div>
            </div>
            <div>
              <span style={{ color: '#a0a0a0' }}>Start Time:</span>
              <div style={{ marginTop: '2px', color: 'white' }}>
                {formatDate(record.start_time, record.currency)}
              </div>
            </div>
            <div>
              <span style={{ color: '#a0a0a0' }}>End Time:</span>
              <div style={{ marginTop: '2px', color: 'white' }}>
                {record.end_time ? formatDate(record.end_time, record.currency) : 'Active'}
              </div>
            </div>
            {record.tier && (
              <div style={{ gridColumn: 'span 2' }}>
                <span style={{ color: '#a0a0a0' }}>Tier:</span>
                <div style={{ marginTop: '2px', color: 'white' }}>
                  {sanitizeText(record.tier)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};



const VolumeTab = ({ barrackId }) => {
  const { isMobile, isTablet } = useResponsive();
  const notification = useNotification();
  const { handleAuthError } = useAuth();

  const [displayVolumeRecords, setDisplayVolumeRecords] = useState([]);
  const [currency, setCurrency] = useState(currencyUtils.defaultCurrency);
  const [summary, setSummary] = useState({
    totalMinutes: 0,
    activeCount: 0,
    averageUsage: 0,
    totalSizeGB: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastShownError, setLastShownError] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    duration: '',
    size: '',
    status: '',
    tier: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 20
  });
  
  // Performance: Initial load tracking
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  
  // Performance: Request cancellation
  
  // Performance: Retry logic
  const maxRetries = 3;
  const retryAttempts = useRef(0);
  
  const prevFiltersRef = useRef(filters);
  const isInitialLoad = useRef(true);

  // Set Sentry context for VolumeTab
  useEffect(() => {
    Sentry.setContext("volume-tab", {
      barrackId,
      isMobile,
      isTablet,
      currentFilters: filters,
      currentPage: pagination.currentPage,
      totalItems: pagination.totalItems,
      currency
    });
  }, [barrackId, isMobile, isTablet, filters, pagination, currency]);

  // Convert errors to toast notifications
  useEffect(() => {
    if (error && error !== lastShownError) {
      notification.error(error);
      setLastShownError(error);
      setError(null);
    }
  }, [error, notification, lastShownError]);

  // Component mount logging
  useEffect(() => {
    logInfo('VolumeTab component mounted', {
      barrackId,
      isMobile,
      isTablet
    });
  }, []);

  

  // Performance: Debounced fetch function
  const debouncedFetchVolumeRecords = useMemo(
    () => debounce(async (currentFilters = filters, page = pagination.currentPage) => {
      await fetchVolumeRecords(currentFilters, page);
    }, 300),
    [filters, pagination.currentPage]
  );

  // Initial load with page 1
  useEffect(() => {
    fetchVolumeRecords(filters, 1);
    isInitialLoad.current = false;
  }, [barrackId]);
  
  // Handle filter changes
  useEffect(() => {
    if (!isInitialLoad.current && JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters)) {
      logUserAction('volume_filters_changed', {
        previousFilters: prevFiltersRef.current,
        newFilters: filters
      });
      fetchVolumeRecords(filters, 1);
      prevFiltersRef.current = {...filters};
    }
  }, [filters]);

  // Calculate duration helper
  const calculateDuration = (durationMinutes) => {
    try {
      if (durationMinutes !== undefined && durationMinutes !== null) {
        const minutes = parseInt(durationMinutes, 10);
        if (isNaN(minutes)) return 'N/A';
        
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = Math.round(minutes % 60);
        return `${hours}h ${remainingMinutes}m`;
      }
      
      return 'N/A';
    } catch (error) {
      logError('Error calculating duration', { durationMinutes }, error);
      return 'N/A';
    }
  };

// Fetch aggregated volume records from backend
const fetchVolumeRecords = async (currentFilters = filters, page = pagination.currentPage) => {
  const startTime = performance.now();
  
  // Check retry limit
  if (retryAttempts.current >= maxRetries) {
    setError('Maximum refresh attempts reached. Please wait before trying again.');
    logError('Maximum volume fetch attempts exceeded', {
      attempts: retryAttempts.current,
      maxRetries
    });
    return;
  }
  
  retryAttempts.current++;
  setIsLoading(true);
  setError(null);
  
  try {
    const params = {
      page: page,
      limit: pagination.limit,
      barrack_id: barrackId
    };
    
    if (currentFilters.startDate) params.start_date = currentFilters.startDate;
    if (currentFilters.endDate) params.end_date = currentFilters.endDate;
    if (currentFilters.size) params.size = currentFilters.size;
    if (currentFilters.status) params.status = currentFilters.status;
    if (currentFilters.tier) params.tier = currentFilters.tier;
    
    if (currentFilters.duration) {
      switch(currentFilters.duration) {
        case 'short':
          params.max_minutes = 60;
          break;
        case 'medium':
          params.min_minutes = 60;
          params.max_minutes = 1440;
          break;
        case 'long':
          params.min_minutes = 1440;
          break;
      }
    }
    
    logInfo('Fetching aggregated volume records with params', { params, barrackId });
    
    const response = await api.getVolumeHourlyBilling(params);
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (response && response.success) {
      const records = response.records || [];
      
      setDisplayVolumeRecords(records);
      
      setPagination(prev => ({
        ...prev,
        currentPage: response.page || page,
        totalPages: response.total_pages || 1,
        totalItems: response.total || 0
      }));
      
      if (response.summary) {
        setSummary(response.summary);
        logInfo('Using backend-calculated summary', response.summary);
      }
      
      if (response.currency) {
        setCurrency(response.currency);
      }
      
      if (!initialDataLoaded) setInitialDataLoaded(true);
      retryAttempts.current = 0; // Reset on success
      
      logPerformance('fetch_volume_records', duration, {
        success: true,
        recordCount: records.length,
        page: page,
        totalItems: response.total,
        barrackId
      });
      
      logInfo('Aggregated volume records loaded successfully', {
        recordCount: records.length,
        page: page,
        totalItems: response.total,
        duration: `${duration.toFixed(2)}ms`
      });
    } else {
      const errorMsg = response.error || 'Failed to fetch volume records';
      throw new Error(errorMsg);
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      logError('Authentication error during volume fetch', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      });
      handleAuthError();
      return;
    }
    
    // Position 2: Use ERROR_MESSAGES lookup if code exists and has mapping
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Volume fetch error', {
        code: err.code,
        status: err.status,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 3: Use interceptor's user-friendly message directly
    if (err.error) {
      setError(err.error);
      logError('Volume fetch error', {
        code: err.code,
        status: err.status,
        message: err.error,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 4: Handle Error objects thrown from success === false case
    if (err instanceof Error && err.message) {
      setError(err.message);
      logError('Volume fetch error', {
        errorMessage: err.message,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 5: Fallback for unexpected error format
    setError('Something went wrong. Please try again.');
    logError('Volume fetch error (unexpected format)', {
      errorType: typeof err,
      duration: `${duration.toFixed(2)}ms`,
      currentFilters,
      page,
      barrackId
    }, err);
    
    logPerformance('fetch_volume_records', duration, {
      success: false,
      error: err.message || 'Unknown error'
    });
  } finally {
    setIsLoading(false);
  }
};
  const applyFilters = (overrideValues = {}) => {
    const updatedFilters = { ...filters, ...overrideValues };
    
    logUserAction('volume_filters_applied', {
      previousFilters: filters,
      newFilters: updatedFilters,
      overrideValues
    });
    
    setFilters(updatedFilters);
  };

  // Reset all filters
  const resetFilters = () => {
    logUserAction('volume_filters_reset_all', {
      previousFilters: filters
    });
    
    setFilters({
      startDate: '',
      endDate: '',
      duration: '',
      size: '',
      status: '',
      tier: ''
    });
  };

  // Handle pagination changes by fetching from backend
  const handlePageChange = (newPage) => {
    if (newPage !== pagination.currentPage && newPage > 0 && newPage <= pagination.totalPages) {
      logUserAction('volume_page_changed', {
        previousPage: pagination.currentPage,
        newPage,
        totalPages: pagination.totalPages,
        totalItems: pagination.totalItems
      });
      
      fetchVolumeRecords(filters, newPage);
    }
  };
  
  // Handle refresh with retry limit
  const handleRefresh = useCallback(async () => {
    if (retryAttempts.current >= maxRetries) {
      const errorMsg = 'Maximum refresh attempts reached. Please wait before trying again.';
      setError(errorMsg);
      
      logError('Maximum refresh attempts exceeded', {
        attempts: retryAttempts.current,
        maxRetries
      });
      
      return;
    }
    
    retryAttempts.current++;
    
    logUserAction('refresh_volumes', {
      attempt: retryAttempts.current,
      maxRetries
    });
    
    logInfo('Refreshing volumes', {
      attempt: retryAttempts.current,
      maxRetries
    });
    
    try {
      await debouncedFetchVolumeRecords();
      retryAttempts.current = 0; // Reset on success
      logInfo('Volume refresh completed successfully');
    } catch (err) {
      if (retryAttempts.current >= maxRetries) {
        const safeMessage = getSafeErrorMessage(err);
        setError(safeMessage);
        
        logError('Volume refresh failed after max retries', {
          attempts: retryAttempts.current,
          maxRetries
        }, err);
      }
    }
  }, [debouncedFetchVolumeRecords]);
  
  // Component unmount cleanup
  useEffect(() => {
    return () => {
      logInfo('VolumeTab component unmounted', {
        barrackId,
        finalState: {
          totalRecords: pagination.totalItems,
          currentPage: pagination.currentPage,
          appliedFilters: filters
        }
      });
    };
  }, []);

  // Render desktop/tablet layout  
  if (!isMobile) {
    return (
      <div>
        <div style={{
          display: 'flex',
          gap: isTablet ? '12px' : '20px',
          marginBottom: '28px',
          flexWrap: isTablet ? 'wrap' : 'nowrap'
        }}>
          {/* Total Usage Card */}
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            border: '1px solid #333333',
            padding: isTablet ? '16px' : '20px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            width: isTablet ? 'calc(50% - 6px)' : 'calc(20% - 20px)',
            height: isTablet ? 'auto' : 'calc(100% - 3cm)'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#a0a0a0',
              fontWeight: '500',
              marginBottom: '10px'
            }}>
              Total Usage
            </div>
            <div style={{
              fontSize: isTablet ? '24px' : '28px',
              fontWeight: '600',
              color: 'white'
            }}>
              {Math.floor(summary.totalMinutes / 60)}h {Math.round(summary.totalMinutes % 60)}m
            </div>
          </div>

     {/* Total Cost Card */} 
<div style={{   
  backgroundColor: '#1a1a1a',   
  borderRadius: '10px',   
  border: '1px solid #333333',   
  padding: isTablet ? '16px' : '20px',   
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',   
  width: isTablet ? 'calc(50% - 6px)' : 'calc(20% - 20px)',   
  height: isTablet ? 'auto' : 'calc(100% - 3cm)',
  marginLeft: isTablet ? '0' : '.2cm'
}}>   
  <div style={{     
    fontSize: '14px',     
    color: '#a0a0a0',     
    fontWeight: '500',     
    marginBottom: '10px'   
  }}>     
    Total Cost   
  </div>   
  <div style={{     
    fontSize: isTablet ? '24px' : '28px',     
    fontWeight: '600',     
    color: '#e0eacc'   
  }}>     
    {currencyUtils.getSymbol(currency)}{safeParseFloat(summary.totalCost).toFixed(2)}   
  </div> 
</div>
          
          {/* Volumes Stats Card */}
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            border: '1px solid #333333',
            padding: isTablet ? '16px' : '20px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            width: isTablet ? 'calc(50% - 6px)' : 'calc(20% - 20px)',
            height: isTablet ? 'auto' : 'calc(100% - 3cm)',
            marginLeft: isTablet ? '0' : '0cm'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{
                  fontSize: '14px',
                  color: '#e0eacc',
                  marginBottom: '4px'
                }}>
                  Count:
                </div>
                <div style={{
                  fontSize: isTablet ? '22px' : '26px',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  {summary.activeCount}
                </div>
              </div>
              <div>
                <div style={{
                  fontSize: '14px',
                  color: '#e0eacc',
                  marginBottom: '4px'
                }}>
                  Total Size:
                </div>
                <div style={{
                  fontSize: isTablet ? '22px' : '26px',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  {summary.totalSizeGB} GB
                </div>
              </div>
            </div>
          </div>
          
          {/* Smart Filter Card */}
          <div style={{
            flex: 1,
            height: isTablet ? 'auto' : 'calc(70%)',
            marginRight: isTablet ? '0' : '-5.5cm',
            width: isTablet ? 'calc(50% - 6px)' : 'auto'
          }}>
            <div style={{ width: isTablet ? '100%' : '400px' }}>
              <VolumeSmartFilterContainer 
                filters={filters}
                setFilters={setFilters}
                applyFilters={applyFilters}
                resetFilters={resetFilters}
                isMobile={isMobile}
                isTablet={isTablet}
              />
            </div>
          </div>
        </div>

        {/* Persistent Error Display with Retry */}
        {error && (
          <div style={{
            marginBottom: '24px',
            backgroundColor: 'rgba(238, 85, 85, 0.1)',
            border: '1px solid rgba(238, 85, 85, 0.3)',
            borderRadius: '8px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
              <AlertTriangle size={20} color="#ff7777" />
              <p style={{ color: '#ff9999', fontSize: '14px', margin: 0 }}>{error}</p>
            </div>
            <button
              onClick={() => {
                setError(null);
                retryAttempts.current = 0;
                handleRefresh();
              }}
              style={{
                backgroundColor: 'rgba(238, 85, 85, 0.2)',
                border: '1px solid rgba(238, 85, 85, 0.4)',
                borderRadius: '4px',
                padding: '8px 16px',
                color: '#ff9999',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(238, 85, 85, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(238, 85, 85, 0.2)';
              }}
            >
              <RefreshCw size={14} />
              Retry
            </button>
          </div>
        )}

        {isLoading ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 0',
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            border: '1px solid #333333',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            color: '#a0a0a0',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}>
            <Loader2 className="animate-spin" size={24} />
            Loading volume records...
          </div>

        ) : (
          <>
            <div style={{
              backgroundColor: '#1a1a1a',
              borderRadius: '10px',
              border: '1px solid #333333',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
              overflowX: 'auto'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: '0'
              }}>
                <thead>
                  <tr>
          
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Volume ID</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Volume Name</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Size</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Tier</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Start Time</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>End Time</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Duration</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Rate</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Total Cost</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayVolumeRecords.length > 0 ? (
                    displayVolumeRecords.map((record) => (
                      <tr 
                        key={record.billing_id}
                        style={{
                          transition: 'background-color 0.15s ease'
                        }}
                      >
              
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                          textAlign: record.volume_id ? 'left' : 'center'
                        }}>{sanitizeText(record.volume_id) || '-'}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>{sanitizeText(record.volume_name)}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>{record.size_gb} GB</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>{sanitizeText(record.tier) || '-'}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>{formatDate(record.start_time, record.currency)}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>{record.end_time ? formatDate(record.end_time, record.currency) : 'Active'}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>{calculateDuration(record.total_duration_minutes)}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>
                          {currencyUtils.getSymbol(record.currency || currency)}
                          {safeParseFloat(record.per_minute_rate || 0).toFixed(5)}/min
                        </td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>
                          {currencyUtils.getSymbol(record.currency || currency)}
                          {safeParseFloat(record.total_cost).toFixed(5)}
                        </td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>
                          <StatusBadge status={record.status} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td 
                        colSpan="10" 
                        style={{
                          textAlign: 'center',
                          color: '#777777',
                          padding: '40px',
                          fontStyle: 'italic'
                        }}
                      >No volume billing records found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {displayVolumeRecords.length > 0 && (
              <Pagination 
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                isMobile={isMobile}
              />
            )}
          </>
        )}
      </div>
    );
  }

  // Mobile layout
  return (
    <div style={{ padding: isMobile ? '12px' : '16px' }}>
      {/* Mobile Summary Cards - 2x2 Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: isMobile ? '8px' : '12px',
        marginBottom: isMobile ? '16px' : '20px'
      }}>
        {/* Total Usage */}
        <div style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333333',
          padding: isMobile ? '12px' : '16px'
        }}>
          <div style={{
            fontSize: isMobile ? '11px' : '12px',
            color: '#a0a0a0',
            fontWeight: '500',
            marginBottom: isMobile ? '6px' : '8px'
          }}>
            Total Usage
          </div>
          <div style={{
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: '600',
            color: 'white'
          }}>
            {Math.floor(summary.totalMinutes / 60)}h {Math.round(summary.totalMinutes % 60)}m
          </div>
        </div>
    {/* Total Cost */}
<div style={{
  backgroundColor: '#1a1a1a',
  borderRadius: '8px',
  border: '1px solid #333333',
  padding: isMobile ? '12px' : '16px'
}}>
  <div style={{
    fontSize: isMobile ? '11px' : '12px',
    color: '#a0a0a0',
    fontWeight: '500',
    marginBottom: isMobile ? '6px' : '8px'
  }}>
    Total Cost
  </div>
  <div style={{
    fontSize: isMobile ? '18px' : '20px',
    fontWeight: '600',
    color: '#e0eacc'
  }}>
    {currencyUtils.getSymbol(currency)}{safeParseFloat(summary.totalCost).toFixed(2)}
  </div>
</div>
        
        {/* Volume Stats */}
        <div style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333333',
          padding: isMobile ? '12px' : '16px'
        }}>
          <div style={{
            fontSize: isMobile ? '11px' : '12px',
            color: '#a0a0a0',
            fontWeight: '500',
            marginBottom: isMobile ? '6px' : '8px'
          }}>
            Count / Total Size
          </div>
          <div style={{
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: '600',
            color: 'white'
          }}>
            {summary.activeCount} / {summary.totalSizeGB} GB
          </div>
        </div>

        {/* Filter container */}
        <div style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333333',
          padding: isMobile ? '8px' : '16px'
        }}>
          <VolumeSmartFilterContainer 
            filters={filters}
            setFilters={setFilters}
            applyFilters={applyFilters}
            resetFilters={resetFilters}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        </div>
      </div>

      {/* Persistent Error Display with Retry */}
      {error && (
        <div style={{
          marginBottom: isMobile ? '16px' : '20px',
          backgroundColor: 'rgba(238, 85, 85, 0.1)',
          border: '1px solid rgba(238, 85, 85, 0.3)',
          borderRadius: '8px',
          padding: isMobile ? '12px' : '16px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: '10px',
            marginBottom: '12px'
          }}>
            <AlertTriangle size={18} color="#ff7777" style={{ flexShrink: 0, marginTop: '2px' }} />
            <p style={{ 
              color: '#ff9999', 
              fontSize: isMobile ? '13px' : '14px', 
              margin: 0,
              flex: 1,
              lineHeight: '1.4'
            }}>
              {error}
            </p>
          </div>
          <button
            onClick={() => {
              setError(null);
              retryAttempts.current = 0;
              handleRefresh();
            }}
            style={{
              backgroundColor: 'rgba(238, 85, 85, 0.2)',
              border: '1px solid rgba(238, 85, 85, 0.4)',
              borderRadius: '4px',
              padding: isMobile ? '10px 16px' : '8px 16px',
              color: '#ff9999',
              fontSize: isMobile ? '13px' : '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              width: '100%'
            }}
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      )}

      {isLoading ? (
        <div style={{
          textAlign: 'center',
          padding: isMobile ? '30px 16px' : '40px 20px',
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333333',
          color: '#a0a0a0',
          fontSize: isMobile ? '13px' : '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? '6px' : '8px'
        }}>
          <Loader2 className="animate-spin" size={isMobile ? 18 : 20} />
          Loading volume records...
        </div>
   
      ) : (
        <>
          {/* Mobile Volume Cards */}
          <div>
            {displayVolumeRecords.length > 0 ? (
              displayVolumeRecords.map((record) => (
                <MobileVolumeCard
                  key={record.billing_id}
                  record={record}
                  currency={currency}
                />
              ))
            ) : (
              <div style={{
                textAlign: 'center',
                color: '#777777',
                padding: isMobile ? '30px 16px' : '40px 20px',
                fontStyle: 'italic',
                backgroundColor: '#1a1a1a',
                borderRadius: '8px',
                border: '1px solid #333333',
                fontSize: isMobile ? '13px' : '14px'
              }}>
                No volume billing records found
              </div>
            )}
          </div>
          
          {displayVolumeRecords.length > 0 && (
            <Pagination 
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              isMobile={isMobile}
            />
          )}
        </>
      )}
    </div>
  );
};

export default VolumeTab;